<script lang="ts" setup>
import ToneSelect from "@/components/mobile/ToneSelect.vue";
import { ref } from "vue";
import { watch } from "vue";
import { useI18n } from "vue-i18n";
import { MediaUploadFileItem } from "@/types/component";
import { UploaderFileListItem } from "vant/es";
import { useTone } from "@/stores/tone";
import { useMedia } from "@/stores/media";
import api from "@/modules/api";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import { MateTone } from "@/types/entity";
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    show: boolean;
    selectedToneId: number;
  }>(),
  {
    show: false,
    selectedToneId: 0,
  }
);

const emit = defineEmits<{
  (e: "update:show"): void;
  (e: "update:selectedToneId", toneId: number): void;
}>();

const toneStore = useTone();
const stopMusic = ref(0);
const toneMoodIdx = ref(0);

const list = [5, 4, 2, 9];

const toneMoodMap: Record<number, number> = {};
list.forEach((v, i) => (toneMoodMap[i] = v));

watch(
  () => props.show,
  (val) => {
    if (!val) stopMusic.value++;
  }
);

const onMediaLoaded = async (
  items: UploaderFileListItem | UploaderFileListItem[]
) => {
  const loading = showLoadingToast({ message: t(`Message.uploading`) });
  const [media] = await useMedia().uploadMedia(
    items as MediaUploadFileItem,
    0,
    1
  );
  const { item } = await api.createOneMateMedia({
    mediaId: media.mediaId,
    type: 3,
  });
  loading.close();
  activeAudioId.value = item.id;
  //@ts-ignore
  toneStore.bgmList.push({
    id: item.id,
    mediaId: item.mediaId,
    //@ts-ignore
    media: { url: media.url! },
    name: item.name || t("Message.untitledMusic"),
    toneMood: 9,
  });
  activeAudioName.value = "";
  showAddAudio.value = true;
};

const showAddAudio = ref(false);
const activeAudioName = ref("");
const activeAudioId = ref(0);

const clickConfirmAudioName = async () => {
  const loading = showLoadingToast({ message: t(`Message.saving`) });
  await api.updateOneMateMedia(activeAudioId.value, activeAudioName.value);
  const idx = toneStore.bgmList.findIndex((v) => v.id === activeAudioId.value);
  if (idx !== -1) toneStore.bgmList[idx].name = activeAudioName.value;
  loading.close();
};

const onEditName = (tone: MateTone) => {
  const { id, name } = tone;
  activeAudioId.value = id;
  activeAudioName.value = name;
  showAddAudio.value = true;
};

const onDeleteMedia = async (tone: MateTone) => {
  showConfirmDialog({
    title: t("ConfirmModal.deleteMusic"),
    message: t("ConfirmModal.deleteMusicText"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(async () => {
    const { id } = tone;
    const loading = showLoadingToast({ message: t(`Message.deleting`) });
    await api.deleteMateMedia(id);
    const idx = toneStore.bgmList.findIndex((v) => v.id === id);
    if (idx !== -1) toneStore.bgmList.splice(idx, 1);
    loading.close();
    showSuccessToast({ message: t("Message.deleteSuccessful") });
  });
};
</script>
<template>
  <VanPopup
    :show="show"
    class="h-[80%] overflow-hidden grid grid-rows-[auto_auto_1fr]"
    position="bottom"
    round
    @update:show="emit('update:show')"
  >
    <div
      class="flex items-center justify-center py-4 border-b border-b-primary px-4"
    >
      <div class="flex-1"></div>
      <div class="flex-1 text-center">
        {{ $t("Buttons.chooseBackgroundMusic") }}
      </div>
      <div class="flex-1 text-right relative">
        <VanUploader
          accept="audio/*"
          :after-read="onMediaLoaded"
          :max-count="1"
          :preview-image="false"
          class="gallery-uploader w-full"
        >
          <button class="btn btn-sm">
            {{ $t("Buttons.uploadMusic") }}
          </button>
        </VanUploader>
      </div>
    </div>
    <VanTabs v-model:active="toneMoodIdx">
      <VanTab v-for="i in list" :title="t(`game.toneMood.${i}`)" />
    </VanTabs>
    <div class="w-full overflow-y-auto flex flex-col relative p-2 gap-2">
      <tone-select
        isBgm
        :tone-mood="toneMoodMap[toneMoodIdx]"
        :model-value="selectedToneId"
        @update:model-value="emit('update:selectedToneId', $event)"
        :stop-music="stopMusic"
        @on-edit-name="onEditName"
        @on-delete-media="onDeleteMedia"
      />
    </div>
  </VanPopup>
  <VanDialog
    v-model:show="showAddAudio"
    :title="$t('game.audioName')"
    show-cancel-button
    :confirm-button-text="$t('Buttons.confirm')"
    :cancel-button-text="$t('Buttons.cancel')"
    @confirm="clickConfirmAudioName"
  >
    <van-field
      class="my-2"
      v-model="activeAudioName"
      :label="$t('game.name')"
      :placeholder="$t('Message.pleaseEnterAudioName')"
    />
  </VanDialog>
</template>
